package org.teddapp.project.ui.notes.ink

/**
 * La lecture de l'écriture manuscrite, fournie par la plateforme qui sait la faire
 * (Android, avec ML Kit). [readLine] reçoit les traits d'une ligne, chacun une suite de
 * points (x, y), et rend le texte lu, ou null.
 */
object HandwritingReader {
    var readLine: (suspend (strokes: List<List<Pair<Float, Float>>>) -> String?)? = null

    val available: Boolean get() = readLine != null

    /**
     * Lit un dessin, ligne par ligne. Le surligneur n'est pas de l'écriture : il est
     * laissé de côté. Rend le texte, lignes séparées par des retours, ou null si rien
     * n'a pu être lu.
     */
    internal suspend fun read(drawing: InkDrawing): String? {
        val reader = readLine ?: return null
        val lines = linesOf(drawing.strokes.filter { it.tool == InkTool.Pen })
        val read = lines.mapNotNull { line ->
            reader(line.map { s -> s.points.map { it.x to it.y } })?.trim()?.takeIf { it.isNotEmpty() }
        }
        return read.joinToString("\n").ifBlank { null }
    }

    /**
     * Range les traits en lignes d'écriture, de haut en bas. Un trait rejoint une ligne
     * quand il en partage l'essentiel de la hauteur ; dans une ligne, les traits gardent
     * l'ordre où ils ont été tracés, celui que la reconnaissance attend.
     */
    internal fun linesOf(strokes: List<InkStroke>): List<List<InkStroke>> {
        class Line(var top: Float, var bottom: Float, val members: MutableList<Pair<Int, InkStroke>>)
        val lines = mutableListOf<Line>()
        strokes.withIndex().sortedBy { (it.value.bounds[1] + it.value.bounds[3]) / 2f }.forEach { (i, s) ->
            val top = s.bounds[1] + s.width
            val bottom = s.bounds[3] - s.width
            val height = (bottom - top).coerceAtLeast(1f)
            val line = lines.firstOrNull { l ->
                val overlap = minOf(bottom, l.bottom) - maxOf(top, l.top)
                overlap > 0.4f * minOf(height, (l.bottom - l.top).coerceAtLeast(1f))
            }
            if (line == null) {
                lines += Line(top, bottom, mutableListOf(i to s))
            } else {
                line.members += i to s
                line.top = minOf(line.top, top)
                line.bottom = maxOf(line.bottom, bottom)
            }
        }
        return lines.sortedBy { it.top }.map { l -> l.members.sortedBy { it.first }.map { it.second } }
    }
}
